import { SharedServicesEnvironment } from '@benefits/shared-services';
import { NgxLoggerLevel } from 'ngx-logger';
import { EnvName } from 'envName';

export interface Environment extends SharedServicesEnvironment {
  api: string;
  appBaseUrl: string;
  clientId: string;
  consoleTelemetryLevel: NgxLoggerLevel;
  countdownSeconds: number;
  icapEnabled: boolean;
  loginOrigin: string;
  profileSettingsUrl: string;
  partnerPortalUrl: string;
  loginUrl: string;
  pageTitleSuffix: string;
  portalTimeoutMilliseconds: number;
  production?: boolean;
  redirectUrl: string;
  goUrl: string;
  serverTelemetryLevel: NgxLoggerLevel;
  telemetryServerUrl: string;
  claimDetailEmployeeCTAUrl: string;
  claimDetailPolicyHolderCTAUrl: string;
  environmentName: EnvName;
  documentDownloadLink: string;
  quicksight: {
    lifeStatusDashboardId: string;
    wopStatusDashboardId: string;
  };
  routeFlags: {
    showAddEmployee?: boolean;
    showAllForms?: boolean;
    showBillingFancy?: boolean;
    showBillingStandard?: boolean;
    showClaimForms?: boolean;
    showClaimReports?: boolean;
    showClaims?: boolean;
    showClaimsSearch?: boolean;
    showClaimsSearchNew?: boolean;
    showContractDocuments?: boolean;
    showCreateClaims?: boolean;
    showDashboard?: boolean;
    showEOIReport?: boolean;
    showEmployeeForms?: boolean;
    showFinancial?: boolean;
    showResources?: boolean;
    showSearchEmployees?: boolean;
    showTaxForms?: boolean;
  };
}

//export const clientIdDefault = '12u6i5aju4tud969omgbkgaf64'; //Cognito
export const clientIdDefault = 'benefits-portal-ui-dev-client' //ICAP
const baseUrlDefault = 'https://localhost';
const portDefaultString = window.location.port ?? 8080;
const portDefault = parseInt(portDefaultString, 10);
const claimDetailEmployeeCTAUrlDefault = 'https://symetralams--uat.my.salesforce.com/';
const claimDetailPolicyHolderCTAUrlDefault = 'https://symetralams--uat.sandbox.my.site.com/portal/s';
const documentDownloadLinkDefault = 'https://localhost:8080';
const quicksightLifeStatusDashboardIdDefault = 'ec06f4d3-8914-4068-90be-2c4c1462aab2';
const quicksightWopStatusDashboardIdDefault = 'b42a4f42-dbaa-435d-96e2-a305bbc8489a';
//if testing DEMO locally to download document, use following link
//const documentDownloadLinkDefault = 'https://bdportal-mocks.bdportal-dev.symetra.cloud';

export const setCommonEnvironmentVariables = (baseUrl: string, port: number, clientId: string): Environment => {
  const appBaseUrl = `${baseUrl}:${port}`;
  const loginOrigin = `${baseUrl}:${port}/login`;
  const goUrl = 'http://benefits.dev.symetra.com/BenefitsPortal';
  //const redirectUrl = `${baseUrl}:${port}/authorize`; //Cognito
  const redirectUrl = `${baseUrl}:${port}/login`; //Icap
  const profileSettingsUrl = 'https://profile.account-dev.symetra.com/';
  const partnerPortalUrl = '';

  const environmentValues = {
    api: 'v1',
    appBaseUrl,
    clientId,
    consoleTelemetryLevel: NgxLoggerLevel.DEBUG,
    countdownSeconds: 300,
    icapEnabled: true,
    loginOrigin,
    profileSettingsUrl,
    partnerPortalUrl,
    //loginUrl: `https://bdportal-dev.auth.us-west-2.amazoncognito.com/authorize?client_id=${clientId}&response_type=code&scope=email+openid+profile&redirect_uri=${redirectUrl}&identity_provider=NET-IQ`, //Cognito
    loginUrl: `https://idp.account-dev.symetra.com/am/oauth2/realms/root/realms/alpha/authorize?client_id=${clientId}&response_type=code&redirect_uri=${encodeURIComponent(
        redirectUrl,
      )}&scope=profile+symetra+email+openid`,
    pageTitleSuffix: 'My Benefits at Symetra',
    portalTimeoutMilliseconds: 1500000,
    production: false,
    redirectUrl,
    goUrl,
    serverTelemetryLevel: NgxLoggerLevel.OFF,
    telemetryServerUrl: '/tattle/',
    claimDetailEmployeeCTAUrl: claimDetailEmployeeCTAUrlDefault,
    claimDetailPolicyHolderCTAUrl: claimDetailPolicyHolderCTAUrlDefault,
    environmentName: EnvName.PRD,
    quicksight: {
      lifeStatusDashboardId: quicksightLifeStatusDashboardIdDefault,
      wopStatusDashboardId: quicksightWopStatusDashboardIdDefault
    },
    documentDownloadLink: documentDownloadLinkDefault,
    routeFlags: {
      showAddEmployee: true,
      showAllForms: true,
      showBillingFancy: false,
      showBillingStandard: true,
      showClaimForms: true,
      showClaimReports: false,
      showClaims: true,
      showClaimsSearch: false,
      showClaimsSearchNew: false,
      showContractDocuments: true,
      showCreateClaims: true,
      showDashboard: true,
      showEOIReport: true,
      showEmployeeForms: true,
      showFinancial: true,
      showResources: true,
      showSearchEmployees: true,
      showTaxForms: true
    }
  };

  return environmentValues;
};

const commonEnvironmentValues = setCommonEnvironmentVariables(baseUrlDefault, portDefault, clientIdDefault);

export class AppEnvironment implements Environment {
  static empty() {
    return new AppEnvironment({
      api: 'v1',
      appBaseUrl: '',
      clientId: '',
      consoleTelemetryLevel: NgxLoggerLevel.OFF,
      countdownSeconds: 0,
      icapEnabled: true,
      loginOrigin: '',
      loginUrl: '',
      profileSettingsUrl: '',
      partnerPortalUrl: '',
      pageTitleSuffix: '',
      portalTimeoutMilliseconds: 0,
      redirectUrl: '',
      goUrl: '',
      serverTelemetryLevel: NgxLoggerLevel.OFF,
      telemetryServerUrl: '',
      claimDetailEmployeeCTAUrl: '',
      claimDetailPolicyHolderCTAUrl: '',
      quicksight: {
        lifeStatusDashboardId: '',
        wopStatusDashboardId: ''
      },
      documentDownloadLink: '',
      routeFlags: {
        showAddEmployee: false,
        showAllForms: false,
        showBillingFancy: false,
        showBillingStandard: false,
        showClaimForms: false,
        showClaimReports: false,
        showClaims: false,
        showClaimsSearch: false,
        showClaimsSearchNew: false,
        showContractDocuments: false,
        showCreateClaims: false,
        showDashboard: false,
        showEOIReport: false,
        showEmployeeForms: false,
        showFinancial: false,
        showResources: false,
        showSearchEmployees: false,
        showTaxForms: false
      }
    });
  }

  api: string;
  appBaseUrl: string;
  clientId: string;
  consoleTelemetryLevel: NgxLoggerLevel;
  countdownSeconds: number;
  icapEnabled: boolean;
  loginOrigin: string;
  profileSettingsUrl: string;
  partnerPortalUrl: string;
  loginUrl: string;
  pageTitleSuffix: string;
  portalTimeoutMilliseconds: number;
  production?: boolean;
  redirectUrl: string;
  goUrl: string;
  serverTelemetryLevel: NgxLoggerLevel;
  telemetryServerUrl: string;
  claimDetailEmployeeCTAUrl: string;
  claimDetailPolicyHolderCTAUrl: string;
  environmentName: EnvName;
  quicksight: {
    lifeStatusDashboardId: string;
    wopStatusDashboardId: string;
  };
  documentDownloadLink: string;
  routeFlags: {
    showAddEmployee?: boolean;
    showAllForms?: boolean;
    showBillingFancy?: boolean;
    showBillingStandard?: boolean;
    showClaimForms?: boolean;
    showClaimReports?: boolean;
    showClaims?: boolean;
    showClaimsSearch?: boolean;
    showClaimsSearchNew?: boolean;
    showContractDocuments?: boolean;
    showCreateClaims?: boolean;
    showDashboard?: boolean;
    showEOIReport?: boolean;
    showEmployeeForms?: boolean;
    showFinancial?: boolean;
    showResources?: boolean;
    showSearchEmployees?: boolean;
    showTaxForms?: boolean;
  };

  constructor(env: Partial<Environment>) {
    const environment: Environment = {
      ...commonEnvironmentValues,
      ...env
    };

    this.api = environment.api;
    this.appBaseUrl = environment.appBaseUrl;
    this.clientId = environment.clientId;
    this.consoleTelemetryLevel = environment.consoleTelemetryLevel;
    this.countdownSeconds = environment.countdownSeconds;
    this.icapEnabled = environment.icapEnabled;
    this.loginOrigin = environment.loginOrigin;
    this.loginUrl = environment.loginUrl;
    this.profileSettingsUrl = environment.profileSettingsUrl;
    this.partnerPortalUrl = environment.partnerPortalUrl;
    this.pageTitleSuffix = environment.pageTitleSuffix;
    this.portalTimeoutMilliseconds = environment.portalTimeoutMilliseconds;
    this.production = environment.production;
    this.redirectUrl = environment.redirectUrl;
    this.goUrl = environment.goUrl;
    this.serverTelemetryLevel = environment.serverTelemetryLevel;
    this.telemetryServerUrl = environment.telemetryServerUrl;
    this.claimDetailEmployeeCTAUrl = environment.claimDetailEmployeeCTAUrl;
    this.claimDetailPolicyHolderCTAUrl = environment.claimDetailPolicyHolderCTAUrl;
    this.environmentName = environment.environmentName;
    this.quicksight = environment.quicksight;
    this.documentDownloadLink = environment.documentDownloadLink;
    this.routeFlags = environment.routeFlags;
  }
}

export const commonEnvironment = new AppEnvironment(commonEnvironmentValues);
